<template>
  <SlideIn name="FilterSlideIn" @open="setStateFromUrlParams">
    <template #slideInHeader>
      <div
        class="sticky top-0 z-50 flex w-full items-center justify-between bg-white pb-2 pt-8 xl:relative xl:p-0"
      >
        <Headline look="h2" tag="p">
          {{ $t('filter.title') }}
        </Headline>
        <FimButton
          type="tertiary"
          is-small
          data-testid="slide-in-close-button"
          @click="toggle"
        >
          <template #iconAfter>
            <LazyIconFielmannClose class="size-6" />
          </template>
        </FimButton>
      </div>
    </template>
    <template #slideInBody>
      <FilterGroup
        v-for="filter in availableFilters"
        :key="filter.slug"
        class="mt-10"
        data-testid="list_filter"
        :label="
          filter.name.toLowerCase() === 'prices'
            ? $t('filter.price')
            : filter.name
        "
        :badge="state?.[filter.slug]?.length || undefined"
        @click:reset="resetFilter(filter.slug)"
      >
        <GlassesWidthSlider
          v-if="filter.slug === 'glassesWidth'"
          v-model="glassesWidthState"
          :min="minMaxGlassesWidth[0]"
          :max="minMaxGlassesWidth[1]"
          :data="glassesWidthValues"
          data-testid="glasses-width-slider"
        />
        <FilterCategorySelect
          v-else-if="filter.slug === 'filterCategory'"
          v-model="state[filter.slug]"
          :filter="filter"
        />
        <MultipleSelectionList
          v-else-if="filter.type === 'attributes'"
          v-model="state[filter.slug]"
          :items="filter.values"
          class="mt-2 space-y-2"
          tag="ul"
        >
          <template #item="{ item }: { item: any }">
            <li class="mb-[5px]">
              <Checkbox
                :id="filter.slug + '-' + item.value"
                v-model="state[filter.slug]"
                :label="
                  filter.slug === 'headWidth'
                    ? $t(`filter.head_width.${item.handle}`)
                    : item.displayName
                "
                :item="item"
                text-size="md"
              >
                <template v-if="filter.iconPath" #icon>
                  <div class="mr-2 flex min-w-[24px] justify-center">
                    <component
                      :is="resolveComponent('Lazy' + item.iconName)"
                      class="max-h-[40px] w-full max-w-[40px]"
                    />
                  </div>
                </template>
              </Checkbox>
            </li>
          </template>
        </MultipleSelectionList>

        <div v-else-if="filter.slug === 'prices'">
          <RangeSlider
            v-model="state.prices"
            :min="minPrice"
            :max="maxPrice"
            :value-formatter="toCurrencyWithoutFractionDigits"
            data-testid="range-slider"
          />
        </div>
        <!-- below not tested or used yet -->
        <template v-if="filter.type === 'boolean'" #action>
          <Toggle v-model="state[filter.slug]" />
        </template>
      </FilterGroup>
    </template>
    <template #slideInActions>
      <div class="flex flex-col gap-2 md:flex-row xl:flex-col">
        <FimButton
          type="tertiary"
          class="w-full"
          data-testid="reset-filter-button"
          @click="resetFilters"
          >{{ $t('filter.delete') }}
        </FimButton>
        <FimButton
          type="primary"
          class="w-full"
          :disabled="productCountData === 0 || !!productCountFetching"
          data-testid="filter-results-button"
          @click="applyFilters"
        >
          <div class="flex items-center justify-center gap-2">
            {{ $t('filter.show_result') }}
            <template v-if="!productCountFetching">
              ({{ productCountData }})
            </template>
            <template v-else>
              (<FimLoadingSpinner class="!h-4 !w-4" />)
            </template>
          </div>
        </FimButton>
      </div>
    </template>
  </SlideIn>
</template>

<script setup lang="ts">
import type { FilterParams } from '@scayle/storefront-nuxt'
import type { FiltersEndpointResponseData } from '@scayle/storefront-api'
import { useFaceWidth } from '~/composables/useFaceWidth'
type Props = {
  filters: FiltersEndpointResponseData
  refreshProductCount: ({ where }: FilterParams) => Promise<void>
  productCountData: number | undefined | null
  productCountFetching: boolean | null
}

const props = withDefaults(defineProps<Props>(), {
  filters: () => [],
  refreshProductCount: async () => {},
  productCountData: undefined,
  productCountFetching: true,
})

const { toggle } = useSlideIn('FilterSlideIn')
const {
  availableFilters,
  setStateFromUrlParams,
  resetFilter,
  glassesWidthState,
  glassesWidthValues,
  minMaxGlassesWidth,
  state,
  minPrice,
  maxPrice,
  toCurrencyWithoutFractionDigits,
  resetFilters,
  applyFilters,
  applyFiltersWithoutToggle,
  setFaceWidthFilter,
} = useFilter(props.filters, props.refreshProductCount)

const { faceWidth } = useFaceWidth()

onMounted(() => {
  setStateFromUrlParams()

  if (faceWidth.value) {
    setFaceWidthFilter(faceWidth.value)
    applyFiltersWithoutToggle()
  }
})

onBeforeUnmount(() => {
  resetFilters()
})

watch(faceWidth, (newWidth, oldWidth) => {
  if (newWidth && newWidth !== oldWidth) {
    oldWidth = newWidth
    setFaceWidthFilter(newWidth)
    applyFiltersWithoutToggle()
  }
})

const totalAppliedFilters = computed(() => {
  if (!state.value) return 0

  return Object.entries(state.value).reduce((total, [key, value]) => {
    // Skip glassesWidth if it's at default values
    if (key === 'glassesWidth') {
      const isDefault =
        value.length === 0 ||
        (glassesWidthState.value[0] === minMaxGlassesWidth.value[0] &&
          glassesWidthState.value[1] === minMaxGlassesWidth.value[1])
      return isDefault ? total : total + 1
    }

    // Skip prices if they're at default values
    if (key === 'prices') {
      const isDefault =
        Array.isArray(value) &&
        value[0] === minPrice.value &&
        value[1] === maxPrice.value
      return isDefault ? total : total + 1
    }

    return total + (Array.isArray(value) ? value.length : 0)
  }, 0)
})

const emit = defineEmits<{
  (e: 'update:appliedFiltersCount', count: number): void
}>()

watch(totalAppliedFilters, (newCount) => {
  emit('update:appliedFiltersCount', newCount)
})
</script>
